/**
 * AuditFilters Component
 * Filters for audit query (store and date range selection)
 *
 * @module MetricDataDevTool/components/AuditFilters
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Checkbox,
  ListItemText,
  ListSubheader
} from '@mui/material';
import AdminButton from 'components/common/AdminButton';
import DateRangeSelector from './DateRangeSelector';
import { useUser } from 'hooks/useUser';

/**
 * AuditFilters component
 * @param {Object} props - Component props
 * @param {Array<string>} props.selectedStores - Currently selected store IDs
 * @param {Function} props.onStoresChange - Callback when store selection changes
 * @param {Function} props.onStoresLoad - Callback when stores are loaded
 * @param {Object} props.dateRange - Current date range {from: Date, to: Date}
 * @param {Function} props.onDateRangeChange - Callback when date range changes
 * @param {boolean} [props.disabled=false] - Whether filters are disabled
 */
export default function AuditFilters({
  selectedStores,
  onStoresChange,
  onStoresLoad,
  dateRange,
  onDateRangeChange,
  disabled = false
}) {
  console.log('🎨 [AuditFilters] Component rendering');

  const { user } = useUser();
  console.log('👤 [AuditFilters] User context:', user);

  const [stores, setStores] = useState([]);
  const [loadingStores, setLoadingStores] = useState(false);

  /**
   * Load stores on mount
   */
  useEffect(() => {
    console.log('🔄 [AuditFilters] useEffect triggered, user.client:', user?.client);

    async function fetchStores() {
      if (!user?.client) {
        console.log('⚠️ [AuditFilters] No client, skipping store fetch');
        return;
      }

      setLoadingStores(true);
      try {
        const { API, graphqlOperation } = await import('aws-amplify');
        const { listStoreRecords } = await import('graphql/queries');

        const response = await API.graphql(graphqlOperation(listStoreRecords));
        const storeItems = response.data.listStoreRecords.items || [];

        console.log('📦 [AuditFilters] Loaded stores:', storeItems.length, storeItems);

        const sortedStores = storeItems.sort((a, b) =>
          (a.storeName || '').localeCompare(b.storeName || '')
        );
        setStores(sortedStores);

        // Notify parent of loaded stores
        if (onStoresLoad) {
          onStoresLoad(sortedStores);
        }

        // Set all stores as selected by default if no stores are selected
        if (selectedStores.length === 0 && sortedStores.length > 0) {
          const allStoreIds = sortedStores.map(store => store.id);
          console.log('✅ [AuditFilters] Auto-selecting all stores:', allStoreIds);
          onStoresChange(allStoreIds);
        }
      } catch (error) {
        console.error('❌ [AuditFilters] Error loading stores:', error);
        setStores([]);
      } finally {
        setLoadingStores(false);
      }
    }

    fetchStores();
  }, [user?.client]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleStoreChange = (event) => {
    const value = event.target.value;
    onStoresChange(typeof value === 'string' ? value.split(',') : value);
  };

  const handleSelectAll = () => {
    const allStoreIds = stores.map(store => store.id);
    onStoresChange(allStoreIds);
  };

  const handleClearAll = () => {
    onStoresChange([]);
  };

  const renderValue = (selected) => {
    if (selected.length === 0) {
      return 'Select Stores';
    }
    if (selected.length === stores.length) {
      return 'All Stores';
    }
    if (selected.length === 1) {
      const store = stores.find(s => s.id === selected[0]);
      return store?.storeName || selected[0];
    }
    return `${selected.length} stores selected`;
  };

  return (
    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
      {/* Store Multi-Selector */}
      <FormControl sx={{ minWidth: 250 }} disabled={disabled || loadingStores}>
        <InputLabel id="audit-store-select-label" sx={{ color: '#7C3AED' }}>Stores</InputLabel>
        <Select
          labelId="audit-store-select-label"
          id="audit-store-select"
          multiple
          value={selectedStores}
          label="Stores"
          onChange={handleStoreChange}
          renderValue={renderValue}
          sx={{
            height: '40px',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#7C3AED',
              borderWidth: '2px'
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#6D28D9'
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#7C3AED'
            }
          }}
          MenuProps={{
            PaperProps: {
              style: {
                maxHeight: 400
              }
            }
          }}
        >
          {loadingStores ? (
            <MenuItem disabled>
              <CircularProgress size={20} /> Loading stores...
            </MenuItem>
          ) : stores.length === 0 ? (
            <MenuItem disabled>No stores available</MenuItem>
          ) : (
            [
              <ListSubheader key="header" sx={{ display: 'flex', gap: 1, py: 1 }}>
                <AdminButton
                  size="small"
                  onClick={handleSelectAll}
                  disabled={disabled || loadingStores}
                  variant="text"
                >
                  Select All
                </AdminButton>
                <AdminButton
                  size="small"
                  onClick={handleClearAll}
                  disabled={disabled || loadingStores}
                  variant="text"
                >
                  Clear All
                </AdminButton>
              </ListSubheader>,
              ...stores.map((store) => (
                <MenuItem key={store.id} value={store.id}>
                  <Checkbox checked={selectedStores.indexOf(store.id) > -1} />
                  <ListItemText primary={store.storeName || store.id} />
                </MenuItem>
              ))
            ]
          )}
        </Select>
      </FormControl>

      {/* Date Range Selector */}
      <DateRangeSelector
        dateRange={dateRange}
        onChange={onDateRangeChange}
        disabled={disabled}
      />
    </Box>
  );
}
